import { inject, injectAsync, onIdle, Service } from '@angular/core';
import { environment } from '@workspace/ng-config';
import { ApiService } from '@workspace/ng-http';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';

@Service()
export class StripeIdentityService {
  private apiService = inject(ApiService);
  private stripeLoader = injectAsync(
    () => import('./stripe-loader.service').then((m) => m.StripeLoaderService),
    { prefetch: onIdle },
  );

  /**
   * Starts the identity verification process.
   * 1. Calls the backend to create a VerificationSession and get the clientSecret.
   * 2. Loads Stripe.js and opens the identity verification modal.
   */
  verifyIdentity(): Observable<{ success: boolean }> {
    return this.apiService
      .user()
      .identityVerification()
      .start()
      .pipe(
        switchMap(({ clientSecret }) => {
          if (!clientSecret) {
            return throwError(() => new Error('No client secret returned from server'));
          }
          return from(this.launchStripeModal(clientSecret));
        }),
        map(() => ({ success: true })),
        catchError((error) => {
          console.error('Stripe Identity Error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Loads Stripe and opens the verification modal.
   */
  private async launchStripeModal(clientSecret: string): Promise<void> {
    const loader = await this.stripeLoader();
    await loader.verifyIdentity(environment.stripe.publishableKey, clientSecret);
  }
}
